import React, { useLayoutEffect, useRef, useState } from "react";
import "./ExperienceCard.css";

const isCurrent = (d) => /present|now/i.test(d || "");

// Logo image when the file exists; otherwise the company's initials.
const loadLogo = (name) => {
  if (!name) return null;
  try {
    return require(`../../assests/images/${name}`);
  } catch (e) {
    return null;
  }
};
const initials = (company) => {
  const words = company.replace(/[^\w\s.]/g, "").split(/\s+/).filter(Boolean);
  // one word (e.g. "iQLabs.iq") -> its first two letters, keeping the casing
  if (words.length === 1) return words[0].slice(0, 2);
  return words.slice(0, 2).map((w) => w[0].toUpperCase()).join("");
};

export default function ExperienceCard({ experience }) {
  const [open, setOpen] = useState(false);
  const [long, setLong] = useState(false);
  const descRef = useRef(null);

  // only offer "Read more" when the clamped text is actually cut off
  useLayoutEffect(() => {
    const measure = () => {
      const el = descRef.current;
      if (el && !open) setLong(el.scrollHeight > el.clientHeight + 2);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open]);
  const logo = loadLogo(experience.logo_path);
  const hasDesc = !!(experience.description || "").trim();
  const current = isCurrent(experience.duration);
  const duration = experience.duration.replace(/PRESENT/i, "Present");

  return (
    <div className="tl-card-wrap">
      <article className="experience-card" style={{ "--c": experience.color }}>
        <header className="exp-head">
          <div className={`exp-logo${logo ? "" : " exp-logo-text"}`}>
            {logo ? (
              <img src={logo} alt={`${experience.company} logo`} />
            ) : (
              <span aria-hidden="true">{initials(experience.company)}</span>
            )}
          </div>
          <div className="exp-titles">
            <h3>{experience.title}</h3>
            {experience.company_url ? (
              <a href={experience.company_url} target="_blank" rel="noopener noreferrer">
                {experience.company} <span aria-hidden="true">↗</span>
              </a>
            ) : (
              <span className="exp-company">{experience.company}</span>
            )}
          </div>
          {current && (
            <span className="chip chip-live">
              <i /> Current
            </span>
          )}
        </header>

        <div className="exp-meta">
          <span className="chip chip-mono">{duration}</span>
          {experience.employment_type && (
            <span className="chip">{experience.employment_type}</span>
          )}
          {experience.location && <span className="chip">{experience.location}</span>}
        </div>

        {experience.skills && experience.skills.length > 0 && (
          <ul className="exp-skills" aria-label="Skills">
            {experience.skills.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        )}

        {hasDesc && (
          <div ref={descRef} className={`exp-desc${open ? "" : " clamped"}${long ? " has-more" : ""}`}>
            <p>{experience.description.trim()}</p>
          </div>
        )}
        {hasDesc && long && (
          <button
            type="button"
            className="exp-toggle"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? "Show less" : "Read more"}
            <span aria-hidden="true">{open ? " ↑" : " ↓"}</span>
          </button>
        )}
      </article>
    </div>
  );
}
